
import { useStore } from "vuex";
import { computed, ComputedRef, onBeforeMount, Ref, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  EnrolledTraining,
  Interest,
  User
} from "../../../domain/entities/entities";
import ProfileSection from "../components/application/ProfileSection.vue";
import UserCourses from "../components/application/UserCourses.vue";
import HistoryOrder from "../components/application/shop/HistoryOrder.vue";
import {
  isCirculoTemplate,
  isDecolaTemplate,
  scrollToTop
} from "../components/mixins";
import router from "../../../src/routes";
import UserEvents from "../components/application/userEvents/UserEvents.vue";
import UserStatistics from "../components/application/userStatistics/UserStatistics.vue";
import { dispatchError } from "../utils/error_handling";
import { removeQuery, updateQuery } from "../utils/routing_utils";
import TrainingIndication from "../components/application/Indication/TrainingIndication.vue";
import FavoriteTraining from "../components/application/favorites/FavoriteTraining.vue";

type Option = {
  name: string;
  param: string;
};

export default {
  components: {
    FavoriteTraining,
    TrainingIndication,
    UserStatistics,
    UserEvents,
    ProfileSection,
    UserCourses,
    HistoryOrder
  },
  setup() {
    const store = useStore();
    const route = useRoute();

    /** Param that tells which tab is open in the down section of the profile page. */
    const currentRouteParam: Ref<string | string[]> = ref(route.params.option);

    /** List of tabs available in the down section of the profile page. */
    const options: Option[] = [
      { name: "Estatísticas", param: "statistics" },
      { name: "Treinamentos", param: "courses" },
      { name: "Eventos", param: "events" },
      isDecolaTemplate()
        ? { name: "Histórico de pedidos", param: "history-order" }
        : { name: "", param: "" },
      { name: "Treinamentos recomendados", param: "indication" },
      { name: "Treinamentos favoritos", param: "favorite" }
    ];

    /** Interests of the current user. */
    const interests: ComputedRef<Interest[]> = computed(() => {
      return store.getters["interest_module/interests"];
    });

    /** Current user and all its information. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"] || new User();
    });

    /** All the courses of the current user (in progress and finished). */
    const trainings: ComputedRef<EnrolledTraining[]> = computed(() => {
      return store.getters["userProfile_module/enrolledTraining"];
    });

    onBeforeMount(async () => {
      await listUserInterests();
      scrollToTop();
    });

    watch(route, () => {
      /** Persists the current route name to set which option is selected in the navigation. */
      currentRouteParam.value = route.params.option;
    });

    /** Function used to change the menu style when the current route is equal or parameter.
     * @param param referring to the parameter that is in the route. */
    const isOptionSelected = (param: string): boolean => {
      return param === String(currentRouteParam.value);
    };

    /** Function to navigate to the selected option. */
    const navigateToOption = (event: Event): void => {
      const selectedOption = (event.target as HTMLSelectElement).value;
      router.push({ name: "profile", params: { option: selectedOption } });
    };

    const selectedOption = computed(() => {
      return router.currentRoute.value.params.option || "chart";
    });

    /** Function responsible for loading user interests.*/
    const listUserInterests = async (): Promise<void> => {
      try {
        await store.dispatch("interest_module/listUserInterest");
      } catch (e) {
        await dispatchError(e);
      }
    };

    watch(
      () => route.query,
      async () => {
        if (route.query.filter === "all") {
          await removeQuery(router, "progress");
        }
      }
    );

    return {
      user,
      interests,
      options,
      trainings,
      isOptionSelected,
      navigateToOption,
      selectedOption,
      isCirculoTemplate
    };
  }
};
